import get from 'lodash/get';
import mapKeys from 'lodash/mapKeys';
import mapValues from 'lodash/mapValues';
import omit from 'lodash/omit';
import { safeParse } from '../../utils/string';

import filterStatesByCountry from './states-filter';
import { transferApplicationRequirementsComparator, transferCreditPoliciesComparator } from './cmsDataMappingHelpers';

const cleanAllowedValuesKeys = (hash) => mapKeys(hash, (_, key) => key.replace(/'/g, ''));

export default class CMSDataMapping {
  constructor(
    cmsData,
    editor,
  ) {
    this.statesVocabulary = get(cmsData, 'vocabularies.state');
    this.countriesVocabulary = get(cmsData, 'vocabularies.country');
    this.vocabularies = get(cmsData, 'vocabularies');
    this.editor = editor;

    this.sortedData = get(cmsData, 'contentTypes', []).reduce((map, contentType) => {
      map[contentType.typeName] = contentType;
      return map;
    }, {});

    /* eslint-disable camelcase */
    const collegeSystems = get(this.sortedData, 'he_transfer_admissions.metaData', []).find((item) => item.field_name === 'field_he_xfer_artic_sys')?.allowed_values;
    // sorts college systems
    this.collegeSystems =
      collegeSystems &&
      Object.keys(collegeSystems)
        .sort()
        .reduce((a, b) => {
          a[b] = collegeSystems[b];
          return a;
        }, {});
    this.specialServicesIdsAndNames =
      get(this.sortedData, 'he_specialservice_us.metaData', []).find((item) => item.field_name === 'field_he_special_services_id_ref')?.allowed_values;
    this.field_he_ld_programs_values =
      get(this.sortedData, 'he_specialservice_us.metaData', []).find((item) => item.field_name === 'field_he_ld_programs')?.allowed_values;
    this.studentOrganizationsIdsAndNames =
      get(this.sortedData, 'he_studentorganization_us.metaData', []).find((item) => item.field_name === 'field_he_orgrepresented')?.allowed_values;
    this.field_he_prefered_computer_type_values =
      get(this.sortedData, 'he_computingenvironment_us.metaData', []).find((item) => item.field_name === 'field_he_prefered_computer_type')?.allowed_values;
    /* eslint-enable camelcase */

    this.specialServicesIds =
      get(this.sortedData, 'he_specialservice_us.fieldValues[0].field_he_special_services_id_ref.und', [])
        .map((item) => item.target_id);

    this.studentOrganizationsIds =
      get(this.sortedData, 'he_studentorganization_us.fieldValues[0].field_he_orgrepresented.und', []).map((item) => item.target_id);

    this.institutionMetaData = get(this.sortedData, 'he_institution_us.metaData', []).reduce((map, field) => {
      map[field.field_name] = field;
      return map;
    }, {});

    this.institutionLevels = cleanAllowedValuesKeys(get(this.institutionMetaData, 'field_he_level.allowed_values'));

    this.institutionControls = cleanAllowedValuesKeys(get(this.institutionMetaData, 'field_he_control_id.allowed_values'));

    this.citySizes = get(this.institutionMetaData, 'field_he_city_size.allowed_values');

    this.undergraduateAdmissionsMetaData = get(this.sortedData, 'he_undergraduate_admissions_us.metaData', []).reduce((map, field) => {
      map[field.field_name] = field;
      return map;
    }, {});

    this.admissionsPolicies = get(this.undergraduateAdmissionsMetaData, 'field_he_admissionspolicy_id.allowed_values');

    this.registrationPolicies = get(this.undergraduateAdmissionsMetaData, 'field_he_registrationpolicy_id.allowed_values');

    this.notificationPolicies = get(this.undergraduateAdmissionsMetaData, 'field_he_notificationpolicy_id.allowed_values');

    this.earlyDecisionsValues = get(this.undergraduateAdmissionsMetaData, 'field_he_earlydecision.allowed_values');

    this.waitingListValues = get(this.undergraduateAdmissionsMetaData, 'field_he_waitinglistused.allowed_values');

    this.fieldHeReligiousAffiliationValues = get(this.institutionMetaData, 'field_he_religious_affiliation.allowed_values');

    this.fieldHeProgramsonlineIdValues = get(this.institutionMetaData, 'field_he_programsonline_id.allowed_values');

    this.fieldHeIntlbaccalaureateplcmidValues = get(this.undergraduateAdmissionsMetaData, 'field_he_intlbaccalaureateplcmid.allowed_values'); // new

    this.fieldHeClepsubjectplacementIdValues = get(this.undergraduateAdmissionsMetaData, 'field_he_clepsubjectplacement_id.allowed_values'); // new

    this.fieldHeCeebadvancedplacementidValues = get(this.undergraduateAdmissionsMetaData, 'field_he_ceebadvancedplacementid.allowed_values'); // new

    this.fieldHeApcreditValues = get(this.undergraduateAdmissionsMetaData, 'field_he_apcredit.allowed_values'); // new

    this.fieldHeIbCreditsValues = get(this.undergraduateAdmissionsMetaData, 'field_he_ib_credits.allowed_values'); // new

    this.fieldHeClepcreditValues = get(this.undergraduateAdmissionsMetaData, 'field_he_clepcredit.allowed_values'); // new

    this.highSchoolMetaData = get(this.sortedData, 'field_he_highschoolprerequisites.metaData', []).reduce((map, field) => {
      map[field.field_name] = field;
      return map;
    }, {});

    this.prerequisiteTypes = get(this.highSchoolMetaData, 'field_he_prerequisitetype.allowed_values');

    this.schoolsportsMetaData = get(this.sortedData, 'he_schoolsport_us.metaData', []).reduce((map, field) => {
      map[field.field_name] = field;
      return map;
    }, {});

    this.sportNames = get(this.schoolsportsMetaData, 'field_he_sport_name.allowed_values');

    this.associationsWomen = get(this.schoolsportsMetaData, 'field_he_assocwomen_id.allowed_values');

    this.associationsMen = get(this.schoolsportsMetaData, 'field_he_assocmen_id.allowed_values');

    this.associationsCOED = get(this.schoolsportsMetaData, 'field_he_assoccoed_id.allowed_values');

    this.divisionsWomen = get(this.schoolsportsMetaData, 'field_he_divwomen_id.allowed_values');

    this.divisionsMen = get(this.schoolsportsMetaData, 'field_he_divmen_id.allowed_values');

    this.divisionsCOED = get(this.schoolsportsMetaData, 'field_he_divcoed_id.allowed_values');

    this.conferencesWomen = get(this.schoolsportsMetaData, 'field_he_confwomen_id.allowed_values');

    this.conferencesMen = get(this.schoolsportsMetaData, 'field_he_confmen_id.allowed_values');

    this.conferencesCOED = get(this.schoolsportsMetaData, 'field_he_divcoed_id.allowed_values');

    this.qualificationMetaData = get(this.sortedData, 'field_he_qualification_data_us.metaData', []).reduce((map, field) => {
      map[field.field_name] = field;
      return map;
    }, {});

    this.qualificationTypes = get(this.qualificationMetaData, 'field_he_qualification_type.allowed_values');

    this.requiredOptRecommend = get(this.qualificationMetaData, 'field_he_requiredoptrecommend.allowed_values');

    this.deadlinesMetaData = get(this.sortedData, 'he_deadline_us.metaData', []).reduce((map, field) => {
      map[field.field_name] = field;
      return map;
    }, {});

    this.deadlineTypes = get(this.deadlinesMetaData, 'field_he_deadlinetype.allowed_values');

    this.applicationFactorMetaData = get(this.sortedData, 'field_he_admission_factor_data.metaData', []).reduce((map, field) => {
      map[field.field_name] = field;
      return map;
    }, {});

    this.admissionFactorTypes = get(this.applicationFactorMetaData, 'field_he_admissionfactortype.allowed_values');

    this.he_undergraduatefinancialaid_us_metaData = get(this.sortedData, 'he_undergraduatefinancialaid_us.metaData', []).reduce((map, field) => {
      map[field.field_name] = field;
      return map;
    }, {}); // new

    this.field_he_qualification_data_us_metaData = get(this.sortedData, 'field_he_qualification_data_us.metaData', []).reduce((map, field) => {
      map[field.field_name] = field;
      return map;
    }, {});

    this.field_he_qualification_type_values = get(this.field_he_qualification_data_us_metaData, 'field_he_qualification_type.allowed_values');
    this.field_he_qualification_grouping_values =
      get(this.field_he_qualification_data_us_metaData, 'field_he_qualification_grouping.allowed_values');
    this.field_he_requiredoptrecommend_values = get(this.field_he_qualification_data_us_metaData, 'field_he_requiredoptrecommend.allowed_values');
  }

  getFieldLabel = (entity, fieldName) => {
    const metaData = get(entity, 'metaData', []);
    const field = metaData.find((item) => get(item, 'field_name', '') === fieldName) || {};
    return get(field, 'label', '');
  }

  getServiceData = (contentType, targetId, key) => {
    const entity = this.sortedData[contentType];
    const item = entity.fieldValues[0];
    const eid = `${entity.cmsType}${(item.nid || item.item_id)}`;

    const data = {
      eid,
      value: this[key].includes(targetId) ? '1': '0',
      targetId,
      name: get(this[`${key}AndNames`], targetId, 'not found'),
      key,
    };

    if (contentType === 'he_specialservice_us') {
      data.field_he_service_type_us = data.value === '1' ? true : null;
    }

    return data;
  };

  getDataByField = (contentType, field, fieldValueOrder=0, valueFieldName='value', optionsKey) => {
    try {
      const entity = this.sortedData[contentType];
      const item = entity.fieldValues[fieldValueOrder];
      const eid = `${entity.cmsType}${(item.nid || item.item_id)}`;
      const value = this.getDataOfItem(item, field, valueFieldName);
      const label = this.getFieldLabel(entity, field);

      if (optionsKey in this) {
        return {
          eid,
          value,
          label,
          options: this[optionsKey],
        };
      }
      return {
        eid,
        value,
        label,
      };
    } catch /* istanbul ignore next */ {
      const entity = get(this.sortedData, contentType, {});
      return { eid: '', value: null, label: this.getFieldLabel(entity, field) };
    }
  };

  getDataOfItem = (item, field, valueFieldName='value') => {
    try {
      return item[field].und[0][valueFieldName];
    } catch {
      return '';
    }
  };

  convertGPA = (gpa) => {
    Object.keys(gpa).forEach((key) => {
      const item = gpa[key];
      if (item.value === null) {
        item.value = '';
      }
      if (typeof item.value === 'string' && item.value.length > 0) {
        item.value = Number(item.value).toFixed(1);
      } else if (typeof item.value === 'number') {
        item.value = item.value.toFixed(1);
      }
    });

    return gpa;
  };

  getTestScores = (byGroup = null) => {
    const cmsType = get(this.sortedData, 'field_he_test_us.cmsType', '');
    const testScores = get(this.sortedData, 'field_he_test_us.fieldValues', [])
      .reduce((map, item) => {
        map[item.item_id] = item;
        return map;
      }, {});
    const testType = get(this.sortedData, 'field_he_test_us.metaData', [])
      .find((type) => type.field_name === 'field_he_testtype_id');
    const allowedValues = get(testType, 'allowed_values', {});
    const types = cleanAllowedValuesKeys(allowedValues);

    const testGroup = get(this.sortedData, 'field_he_test_us.metaData', [])
      .find((type) => type.field_name === 'field_he_testgroup_id');
    const allowedGroups = get(testGroup, 'allowed_values', {});
    const groups = cleanAllowedValuesKeys(allowedGroups);

    const allTestScores = Object.keys(testScores).map((id) => {
      const score = testScores[id];
      const eid = `${cmsType}${id}`;
      const value = get(score, 'field_he_testtype_id.und[0].value', 0);
      const type = types[value];
      const groupValue = get(score, 'field_he_testgroup_id.und[0].value', 0);
      const group = groups[groupValue];
      return {
        eid,
        type,
        25: get(score, 'field_he_low.und[0].value'),
        50: get(score, 'field_he_average.und[0].value'),
        75: get(score, 'field_he_high_score.und[0].value'),
        field_he_testgroup_id: group,
        field_he_testtype_id: type,
        field_he_low: get(score, 'field_he_low.und[0].value'),
        field_he_average: get(score, 'field_he_average.und[0].value'),
        field_he_high_score: get(score, 'field_he_high_score.und[0].value'),
      };
    });

    if (byGroup) {
      return allTestScores.filter(
        (item) => item.field_he_testgroup_id === byGroup,
      );
    }
    return allTestScores;
  };

  getAtletics = () => {
    const sportItems = this.sortedData.he_schoolsport_us.fieldValues;

    return sportItems.map((item) => ({
      type: this.getDataOfItem(item, 'field_he_sport_name'),
      name: this.sportNames[this.getDataOfItem(item, 'field_he_sport_name')],
      eid: `${this.sortedData.he_schoolsport_us.cmsType}${item.nid}`,
      men: {
        association: this.getDataOfItem(item, 'field_he_assocmen_id'),
        division: this.getDataOfItem(item, 'field_he_divmen_id'),
        conference: this.getDataOfItem(item, 'field_he_confmen_id'),
        others: {
          club: this.getDataOfItem(item, 'field_he_clubmen'),
          intramural: this.getDataOfItem(item, 'field_he_intramuralmen'),
        },
      },
      women: {
        association: this.getDataOfItem(item, 'field_he_assocwomen_id'),
        division: this.getDataOfItem(item, 'field_he_divwomen_id'),
        conference: this.getDataOfItem(item, 'field_he_confwomen_id'),
        others: {
          club: this.getDataOfItem(item, 'field_he_clubwomen'),
          intramural: this.getDataOfItem(item, 'field_he_intramuralwomen'),
        },
      },
      coed: {
        association: this.getDataOfItem(item, 'field_he_assoccoed_id'),
        division: this.getDataOfItem(item, 'field_he_divcoed_id'),
        conference: this.getDataOfItem(item, 'field_he_confcoed_id'),
        others: {
          club: this.getDataOfItem(item, 'field_he_clubcoed'),
          intramural: this.getDataOfItem(item, 'field_he_intramuralcoed'),
        },
      },
    }));
  };

  getDeadlines = (deadlineTypes) => {
    const deadlineItems = this.sortedData.he_deadline_us.fieldValues;

    return deadlineItems.map((item) => ({
      eid: `${this.sortedData.he_deadline_us.cmsType}${item.nid}`,
      type: this.getDataOfItem(item, 'field_he_deadlinetype'),
      typeName: deadlineTypes[this.getDataOfItem(item, 'field_he_deadlinetype')],
      month: this.getDataOfItem(item, 'field_he_month_of_the_deadline'),
      day: this.getDataOfItem(item, 'field_he_day_of_the_deadline'),
      description: this.getDataOfItem(item, 'field_he_typeotherdescription'),
      isCommonApp: this.getDataOfItem(item, 'field_he_deadline_is_ca') === '1',
    }));
  };

  getOrganizations = () => { // new
    const organizations = [];
    for (let i = 0; i < this.studentOrganizationsIds.length; i += 1) {
      const organization = this.getServiceData('he_studentorganization_us', this.studentOrganizationsIds[i], 'studentOrganizationsIds');
      /* istanbul ignore else */
      if (organization.value === '1') {
        organizations.push({
          name: organization.name,
          field_he_active: organization.value,
        });
      }
    }
    return organizations;
  };

  getAllQualificationData = () => { // new
    const qualifiactionData = this.sortedData.field_he_qualification_data_us.fieldValues;
    const cmsType = get(this.sortedData, 'field_he_qualification_data_us.cmsType', '');
    const allowedTypes = this.field_he_qualification_type_values;
    const allowedGroupings = this.field_he_qualification_grouping_values;
    const allowedValues = this.field_he_requiredoptrecommend_values;
    return qualifiactionData.map((item) => {
      const eid = `${cmsType}${item.item_id}`;
      const type = this.getDataOfItem(item, 'field_he_qualification_type');
      const value = this.getDataOfItem(item, 'field_he_requiredoptrecommend');
      const group = this.getDataOfItem(item, 'field_he_qualification_grouping');

      return {
        eid,
        type,
        value,
        group,
        field_he_qualification_type: allowedTypes[type],
        field_he_qualification_grouping: allowedGroupings[group],
        field_he_requiredoptrecommend: allowedValues[value],
      };
    });
  };

  getApplicationRequirements = (group) => {
    const qualifiactionData = this.sortedData.field_he_qualification_data_us.fieldValues;
    const cmsType = get(this.sortedData, 'field_he_qualification_data_us.cmsType', '');
    const requirementsGroup = qualifiactionData.filter(
      (item) => this.getDataOfItem(item, 'field_he_qualification_grouping') === group.toString(),
    );

    return requirementsGroup.map((item) => ({
      eid: `${cmsType}${item.item_id}`,
      type: this.getDataOfItem(item, 'field_he_qualification_type'),
      value: this.getDataOfItem(item, 'field_he_requiredoptrecommend'),
    }));
  };

  getApplicationFactors = () => {
    const cmsType = get(this.sortedData, 'field_he_admission_factor_data.cmsType', '');
    const items = this.sortedData.field_he_admission_factor_data.fieldValues;

    return items.map((item) => {
      const type = this.getDataOfItem(item, 'field_he_admissionfactortype');
      const importance = this.getDataOfItem(item, 'field_he_importance');

      return {
        eid: `${cmsType}${item.item_id}`,
        type,
        importance,
        field_he_admissionfactortype: get(this, 'applicationFactorMetaData.field_he_admissionfactortype.allowed_values', {})[type],
        field_he_importance: get(this, 'applicationFactorMetaData.field_he_importance.allowed_values', {})[importance],
      };
    });
  };

  getRecommendedCourses = (prerequisiteTypes) => {
    const cmsType = get(this.sortedData, 'field_he_highschoolprerequisites.cmsType', '');
    const items = this.sortedData.field_he_highschoolprerequisites.fieldValues;

    return items.map((item) => {
      const preReq = {
        eid: `${cmsType}${item.item_id}`,
        type: this.getDataOfItem(item, 'field_he_prerequisitetype'),
        recomendationYears: this.getDataOfItem(item, 'field_he_yearsrecommended'),
        requiredYears: this.getDataOfItem(item, 'field_he_yearsrequired'),
      };
      preReq.field_he_prerequisitetype = prerequisiteTypes[preReq.type]; // new
      preReq.field_he_yearsrecommended = preReq.recomendationYears || null; // new
      preReq.field_he_yearsrequired = preReq.requiredYears || null; // new

      return preReq;
    });
  };

  getContactInfo = (contactType) => {
    const contentType = 'he_contact';
    const dataToFieldsMap = [
      { namePrefix: 'field_nameprefix' },
      { firstName: 'field_firstname' },
      { middleName: 'field_middlename' },
      { lastName: 'field_lastname' },
      { jobTitle: 'field_jobtitle' },
      { jobDescription: 'field_jobdescription' },
      { country: 'field_country_ref' },
      { state: 'field_state_ref' },
      { city: 'field_city' },
      { zip: 'field_postalcode' },
      { street1: 'field_addressline1' },
      { street2: 'field_addressline2' },
      { email: 'field_emailaddress' },
      { fax: 'field_faxnumber' },
      { phoneNumber: 'field_phonenumber' },
      { tollFreeNumber: 'field_tollfreenumber' },
      { url: 'field_url' },
    ];

    let ref;
    if (contactType === 'financialaid') {
      const item = this.sortedData.he_undergraduatefinancialaid_us.fieldValues[0];
      ref = this.getDataOfItem(item, 'field_he_finaidcontact_ref', 'target_id');
    } else {
      const item = this.sortedData.he_undergraduate_admissions_us.fieldValues[0];
      ref = this.getDataOfItem(item, 'field_he_admissions_contact_ref', 'target_id');
    }
    const order = this.sortedData[contentType]
      .fieldValues.findIndex((contact) => contact.nid === ref);

    if (!ref || order < 0) {
      // Create a new entity since it doesnot exist
      const fields = {};
      dataToFieldsMap.forEach((map) => {
        fields[map[Object.keys(map)[0]]] = [{ value: '' }];
      });
      const contactEntityCollection = this.editor.getEntityCollectionByType(contentType, fields);
      const entity = contactEntityCollection.addEntity(fields);

      const contactRef = contactType === 'financialaid' ? 'field_he_finaidcontact_ref' : 'field_he_admissions_contact_ref';
      const contactRefParent = contactType === 'financialaid' ? 'he_undergraduatefinancialaid_us' : 'he_undergraduate_admissions_us';
      const contactRefEntityCollection = this.editor.getEntityCollectionByType(contactRefParent, {});
      const contactRefEntities = contactRefEntityCollection.getEntities();
      /* istanbul ignore else */
      if (contactRefEntities && contactRefEntities.length > 0) {
        this.editor.setValue(contactRefEntities[0].eid, contactRef, [{ target_id: entity.eid.replace('node', '') }], true);
      } else {
        contactRefEntityCollection.addEntity({ [contactRef]: [{ target_id: entity.eid.replace('node', '') }] });
      }

      const entityData = {};
      const stateOptions = filterStatesByCountry(undefined, this.countriesVocabulary, this.statesVocabulary);

      dataToFieldsMap.forEach((map) => {
        const key = Object.keys(map)[0];
        entityData[key] = {
          eid: entity.eid,
          value: '',
          label: this.getFieldLabel(this.sortedData[contentType], map[key]),
          options: key === 'state' ? stateOptions : key === 'country' && this.countriesVocabulary,
        };
      });
      return entityData;
    }

    // initialize state options as filtered by country
    const country = this.getDataByField(contentType, 'field_country_ref', order, 'tid', 'countriesVocabulary');
    const state = this.getDataByField(contentType, 'field_state_ref', order, 'tid');
    const stateOptions = filterStatesByCountry(country.value, this.countriesVocabulary, this.statesVocabulary);

    const data = {};
    dataToFieldsMap.forEach((map) => {
      const key = Object.keys(map)[0];
      data[key] = this.getDataByField(contentType, map[key], order);
      if (key === 'state') {
        data[key] = {
          ...state,
          options: stateOptions,
        };
      } else if (key === 'country') {
        data[key] = country;
      }
    });
    return data;
  };

  getApplicationMailingAddress = () => {
    const contentType = 'he_undergraduate_admissions_us';

    // initialize state options as filtered by country
    const country = this.getDataByField(contentType, 'field_country_ref', 0, 'tid', 'countriesVocabulary');
    const state = this.getDataByField(contentType, 'field_state_ref', 0, 'tid');
    const stateOptions = filterStatesByCountry(country.value, this.countriesVocabulary, this.statesVocabulary);

    return {
      country,
      state: {
        ...state,
        options: stateOptions,
      },
      city: this.getDataByField(contentType, 'field_city'),
      zip: this.getDataByField(contentType, 'field_postalcode'),
      street1: this.getDataByField(contentType, 'field_addressline1'),
      street2: this.getDataByField(contentType, 'field_addressline2'),
      email: this.getDataByField(contentType, 'field_emailaddress'),
      fax: this.getDataByField(contentType, 'field_faxnumber'),
      phoneNumber: this.getDataByField(contentType, 'field_phonenumber'),
      tollFreeNumber: this.getDataByField(contentType, 'field_tollfreenumber'),
      url: this.getDataByField(contentType, 'field_url'),
    };
  };

  getSchoolTypes = (includeOptions) => ({
    level: this.getDataByField('he_institution_us', 'field_he_level', 0, 'value', includeOptions ? 'institutionLevels' : null),
    control: this.getDataByField('he_institution_us', 'field_he_control_id', 0, 'value', includeOptions ? 'institutionControls' : null),
  });

  getOverview = () => {
    const { fieldHeReligiousAffiliationValues } = this; // new

    return {
      openingStatement: this.getDataByField('he_institution_us', 'field_he_missionstatement'),
      website: this.getDataByField('he_institution_us', 'field_url'),
      schoolType: this.getSchoolTypes(true),
      undergraduateEnrollment: {
        women: this.getDataByField('he_student_body_us', 'field_he_numugfemales'),
        men: this.getDataByField('he_student_body_us', 'field_he_numugmales'),
      },
      studentFacultyRatio: this.getDataByField('he_student_body_us', 'field_he_studentfacultyratio'),
      campusSurroundings: {
        citySize: this.getDataByField('he_institution_us', 'field_he_city_size', 0, 'value', 'citySizes'),
        citySizeOptions: this.citySizes,
      },
      testScores: this.getTestScores(),
      field_he_test_us: this.getTestScores('Undergraduate'), // new
      gpa: this.convertGPA({
        25: this.getDataByField('he_undergraduate_admissions_us', 'field_he_gpa_25th_percentile'),
        50: this.getDataByField('he_undergraduate_admissions_us', 'field_he_gpaaverage'),
        75: this.getDataByField('he_undergraduate_admissions_us', 'field_he_gpa_75th_percentile'),
      }),

      applicationMailingAddress: this.getApplicationMailingAddress(),
      admissionsOffice: this.getContactInfo('admissions'),
      financialAid: this.getContactInfo('financialaid'),
      field_he_location: { // new
        lat: this.getDataByField('he_institution_us', 'field_he_location', 0, 'lat'),
        lon: this.getDataByField('he_institution_us', 'field_he_location', 0, 'lon'),
      },
      field_he_religious_affiliation: fieldHeReligiousAffiliationValues[
        this.getDataByField('he_institution_us', 'field_he_religious_affiliation').value
      ], // new
      field_he_navianceid: this.getDataByField('he_institution_us', 'field_he_navianceid'),
      field_he_lgbt_rating: this.getDataByField('he_institution_us', 'field_he_lgbt_rating'),
      field_he_institution_id: this.getDataByField('he_institution_us', 'field_he_institution_id'),
      field_he_avg_class_size: this.getDataByField('he_institution_us', 'field_he_avg_class_size'),
      field_he_act_code: this.getDataByField('he_institution_us', 'field_he_act_code'),
      field_he_programsonline_id: {
        ...this.getDataByField('he_institution_us', 'field_he_programsonline_id'),
        options: this.fieldHeProgramsonlineIdValues,
      },
    };
  };

  getStudies = () => ({
    studentFacultyRatio: this.getDataByField('he_student_body_us', 'field_he_studentfacultyratio'),
    studentRetention: this.getDataByField('he_undergraduate_admissions_us', 'field_he_retentionratefulltime'),
    field_he_gradratebachelors4yrs: this.getDataByField('he_undergraduate_admissions_us', 'field_he_gradratebachelors4yrs'),
    field_he_gradratebachelors6yrs: this.getDataByField('he_undergraduate_admissions_us', 'field_he_gradratebachelors6yrs'),

    topAreasOfStudy: {
      1: this.getDataByField('he_student_body_us', 'field_he_popularareaofstudy1'),
      2: this.getDataByField('he_student_body_us', 'field_he_popularareaofstudy2'),
      3: this.getDataByField('he_student_body_us', 'field_he_popularareaofstudy3'),
      4: this.getDataByField('he_student_body_us', 'field_he_popularareaofstudy4'),
      5: this.getDataByField('he_student_body_us', 'field_he_popularareaofstudy5'),
    },

    studyOptions: {
      studyAbroadCredit: this.getServiceData('he_specialservice_us', '47', 'specialServicesIds'),
      studyAbroadNonCredit: this.getServiceData('he_specialservice_us', '48', 'specialServicesIds'),
      graduateStudyAbroadCredit: this.getServiceData('he_specialservice_us', '49', 'specialServicesIds'),
      graduateStudyAbroadNonCredit: this.getServiceData('he_specialservice_us', '50', 'specialServicesIds'),
      studyAbroad: this.getServiceData('he_specialservice_us', '51', 'specialServicesIds'),
      graduateStudyAbroad: this.getServiceData('he_specialservice_us', '52', 'specialServicesIds'),
      teacherCertificationPrograms: this.getDataByField('he_specialservice_us', 'field_he_teachercertpgms'),
      combinedDegreePrograms: this.getDataByField('he_specialservice_us', 'field_he_combineddegreepgms'),
      domesticStudyAwayPrograms: this.getDataByField('he_specialservice_us', 'field_he_domstudyawaypgms'),
      weekendEveningPrograms: this.getDataByField('he_specialservice_us', 'field_he_weekendeveningpgms'),
      acceleratedProgram: this.getDataByField('he_specialservice_us', 'field_he_acceleratedprogram'),
      doubleMajor: this.getDataByField('he_specialservice_us', 'field_he_doublemajor'),
      externalDegreeProgram: this.getDataByField('he_specialservice_us', 'field_he_externaldegreeprogram'),
      honorsProgram: this.getDataByField('he_specialservice_us', 'field_he_honorsprogram'),
      independentStudy: this.getDataByField('he_specialservice_us', 'field_he_independentstudy'),
      internships: this.getDataByField('he_specialservice_us', 'field_he_internships'),
      weekendCollege: this.getDataByField('he_specialservice_us', 'field_he_weekendcollege'),
      cooperativeEducationPrograms: this.getDataByField('he_specialservice_us', 'field_he_cooperativeedupgms'),
    },
  });

  getStudentLife = () => ({
    schoolSize: {
      undergraduateWomen: this.getDataByField('he_student_body_us', 'field_he_numugfemales'),
      undergraduateMen: this.getDataByField('he_student_body_us', 'field_he_numugmales'),
      graduateWomen: this.getDataByField('he_student_body_us', 'field_he_numgradfemales'),
      graduateMen: this.getDataByField('he_student_body_us', 'field_he_numgradmales'),
    },

    nearestCity: {
      city: this.getDataByField('he_institution_us', 'field_he_nearestcity'),
      distance: this.getDataByField('he_institution_us', 'field_he_nearestmajorcitymiles'),
    },

    ethnicity: {
      africanAmerican: this.getDataByField('he_student_body_us', 'field_he_pctafricanam'),
      africanHispanic: this.getDataByField('he_student_body_us', 'field_he_pcthispanic'),
      nativePacificIslander: this.getDataByField('he_student_body_us', 'field_he_pctnativepacificisland'),
      twoOrMoreRaces: this.getDataByField('he_student_body_us', 'field_he_pcttwoormoreraces'),
      caucasian: this.getDataByField('he_student_body_us', 'field_he_pctcaucasian'),
      asian: this.getDataByField('he_student_body_us', 'field_he_pctasian'),
      nativeAmerican: this.getDataByField('he_student_body_us', 'field_he_pctnativeam'),
      nativeUnknown: this.getDataByField('he_student_body_us', 'field_he_pctraceunknown'),
      nonResidentAlien: this.getDataByField('he_student_body_us', 'field_he_pctnonresidentalien'),
      international: this.getDataByField('he_student_body_us', 'field_he_pctinternational'),
    },

    ageData: this.getDataByField('he_student_body_us', 'field_he_pctstudentsgt24yrsold'),

    housingData: {
      field_he_pctoutofstate: this.getDataByField('he_student_body_us', 'field_he_pctoutofstate'), // new
      campusHousingCapacity: this.getDataByField('he_housing_us', 'field_he_camphouscap'),
      liveOnCampus: this.getDataByField('he_student_body_us', 'field_he_pctliveoncamp'),
      freshmanMustLiveOnCampus: this.getDataByField('he_housing_us', 'field_he_freshmenmustliveoncampu'),
      sophomoresMustLiveOnCampus: this.getDataByField('he_housing_us', 'field_he_sophomoresmustliveoncam'),
      juniorsMustLiveOnCampus: this.getDataByField('he_housing_us', 'field_he_juniorsmustliveoncampus'),
      seniorsMustLiveOnCampus: this.getDataByField('he_housing_us', 'field_he_seniorsmustliveoncampus'),
    },

    greekLife: {
      fraternities: this.getDataByField('he_studentorganization_us', 'field_he_numsocialfrat'),
      sororities: this.getDataByField('he_studentorganization_us', 'field_he_numsocialsor'),
    },

    field_he_ld_programs: {
      ...this.getDataByField('he_specialservice_us', 'field_he_ld_programs'),
      options: this.field_he_ld_programs_values,
    },

    services: {
      academicCareerCounseling: this.getServiceData('he_specialservice_us', '15', 'specialServicesIds'),
      accommodationsForStudentsWithADHD: this.getServiceData('he_specialservice_us', '16', 'specialServicesIds'),
      assistanceForHearingImpaired: this.getServiceData('he_specialservice_us', '21', 'specialServicesIds'),
      assistanceForMobilityImpaired: this.getServiceData('he_specialservice_us', '22', 'specialServicesIds'),
      assistanceForVisuallyImpaired: this.getServiceData('he_specialservice_us', '23', 'specialServicesIds'),
      bookOnTapeForRFBD: this.getServiceData('he_specialservice_us', '24', 'specialServicesIds'),
      calculatorsAllowedInExams: this.getServiceData('he_specialservice_us', '25', 'specialServicesIds'),
      computersAllowedInExams: this.getServiceData('he_specialservice_us', '26', 'specialServicesIds'),
      dayCareServices: this.getServiceData('he_specialservice_us', '27', 'specialServicesIds'),
      dictionaryAllowedInExams: this.getServiceData('he_specialservice_us', '28', 'specialServicesIds'),
      ESL: this.getServiceData('he_specialservice_us', '29', 'specialServicesIds'),
      healthServicesAvailable: this.getServiceData('he_specialservice_us', '30', 'specialServicesIds'),
      internshipsWithPay: this.getServiceData('he_specialservice_us', '31', 'specialServicesIds'),
      internshipsWithoutPay: this.getServiceData('he_specialservice_us', '32', 'specialServicesIds'),
      jobPlacement: this.getServiceData('he_specialservice_us', '33', 'specialServicesIds'),
      LDSpecialists: this.getServiceData('he_specialservice_us', '34', 'specialServicesIds'),
      learningCenter: this.getServiceData('he_specialservice_us', '35', 'specialServicesIds'),
      libraryFacilities: this.getServiceData('he_specialservice_us', '36', 'specialServicesIds'),
      peerTutors: this.getServiceData('he_specialservice_us', '37', 'specialServicesIds'),
      professionalTutors: this.getServiceData('he_specialservice_us', '38', 'specialServicesIds'),
      readingMachine: this.getServiceData('he_specialservice_us', '39', 'specialServicesIds'),
      remedialServices: this.getServiceData('he_specialservice_us', '40', 'specialServicesIds'),
      spellcheckerAllowedInExam: this.getServiceData('he_specialservice_us', '41', 'specialServicesIds'),
      studentEmployment: this.getServiceData('he_specialservice_us', '42', 'specialServicesIds'),
      tapeRecordingInClass: this.getServiceData('he_specialservice_us', '43', 'specialServicesIds'),
      tapingOfBooksNotFromRFBD: this.getServiceData('he_specialservice_us', '45', 'specialServicesIds'),
      veteran: this.getServiceData('he_specialservice_us', '46', 'specialServicesIds'),
      aspergersAutismSupportServices: this.getServiceData('he_specialservice_us', '8965', 'specialServicesIds'),
      ROTCArmy: this.getDataByField('he_specialservice_us', 'field_he_rotcarmy'),
      ROTCNavy: this.getDataByField('he_specialservice_us', 'field_he_rotcnavy'),
      ROTCAirforce: this.getDataByField('he_specialservice_us', 'field_he_rotcair'),
      ROTCServicepersonOpportunityCollege: this.getDataByField('he_specialservice_us', 'field_he_rotcservperoppcollege'),
      field_he_counseling_services: this.getDataByField('he_specialservice_us', 'field_he_counseling_services'),
    },

    computingResources: {
      numberOfMacsInComputerCenters: this.getDataByField('he_computingenvironment_us', 'field_he_macsincomputercen'),
      numberOfPCsInComputerCenters: this.getDataByField('he_computingenvironment_us', 'field_he_pcsincomputercenters'),
      numberOfMacsInDepartmentLabs: this.getDataByField('he_computingenvironment_us', 'field_he_macsindepartmentlabs'),
      numberOfPCsInDepartmentLabs: this.getDataByField('he_computingenvironment_us', 'field_he_pcsindepartmentlabs'),
      numberOfMacsInLibraries: this.getDataByField('he_computingenvironment_us', 'field_he_macsinlibrary'),
      numberOfPCsInLibraries: this.getDataByField('he_computingenvironment_us', 'field_he_pcsinlibrary'),
      numberOfMacsInStudentCenters: this.getDataByField('he_computingenvironment_us', 'field_he_macsinstudentcenters'),
      numberOfPCsInStudentCenters: this.getDataByField('he_computingenvironment_us', 'field_he_pcsinstudentcenters'),
      numberOfMacsInResidences: this.getDataByField('he_computingenvironment_us', 'field_he_macsinresidences'),
      numberOfPCsInResidences: this.getDataByField('he_computingenvironment_us', 'field_he_pcsinresidences'),
      numberOfMacsInOther: this.getDataByField('he_computingenvironment_us', 'field_he_macsinother'),
      numberOfPCsInOther: this.getDataByField('he_computingenvironment_us', 'field_he_pcsinother'),
      internetAccessInLibrary: this.getDataByField('he_computingenvironment_us', 'field_he_internetinlibrary'),
      wirelessInternetAccess: this.getDataByField('he_computingenvironment_us', 'field_he_internetwireless'),
      internetAccessOnCentralCampus: this.getDataByField('he_computingenvironment_us', 'field_he_internetincentralloc'),
      internetAccessInResidenceHalls: this.getDataByField('he_computingenvironment_us', 'field_he_internetinresidencehall'),
      computerRequired: this.getDataByField('he_computingenvironment_us', 'field_he_computerrequired'),
      computerRequiredReason: this.getDataByField('he_computingenvironment_us', 'field_he_computerrequiredreason'),
      computerPurchaseOffered: this.getDataByField('he_computingenvironment_us', 'field_he_purchaseplanoffered'),
      preferredComputerType: this.getDataByField('he_computingenvironment_us', 'field_he_prefered_computer_type'),
    },

    organizations: {
      academicClubs: this.getServiceData('he_studentorganization_us', '4487', 'studentOrganizationsIds'),
      academicFratSor: this.getServiceData('he_studentorganization_us', '4488', 'studentOrganizationsIds'),
      business: this.getServiceData('he_studentorganization_us', '4489', 'studentOrganizationsIds'),
      choir: this.getServiceData('he_studentorganization_us', '4490', 'studentOrganizationsIds'),
      commuter: this.getServiceData('he_studentorganization_us', '4491', 'studentOrganizationsIds'),
      concertBand: this.getServiceData('he_studentorganization_us', '4492', 'studentOrganizationsIds'),
      environmental: this.getServiceData('he_studentorganization_us', '4493', 'studentOrganizationsIds'),
      gayAndLesbian: this.getServiceData('he_studentorganization_us', '4494', 'studentOrganizationsIds'),
      honorsAcademic: this.getServiceData('he_studentorganization_us', '4495', 'studentOrganizationsIds'),
      international: this.getServiceData('he_studentorganization_us', '4496', 'studentOrganizationsIds'),
      jazzBand: this.getServiceData('he_studentorganization_us', '4497', 'studentOrganizationsIds'),
      literaryMagazine: this.getServiceData('he_studentorganization_us', '4498', 'studentOrganizationsIds'),
      marchingBand: this.getServiceData('he_studentorganization_us', '4499', 'studentOrganizationsIds'),
      minorityCultural: this.getServiceData('he_studentorganization_us', '4500', 'studentOrganizationsIds'),
      minorityStudent: this.getServiceData('he_studentorganization_us', '4501', 'studentOrganizationsIds'),
      newspaper: this.getServiceData('he_studentorganization_us', '4502', 'studentOrganizationsIds'),
      orchestra: this.getServiceData('he_studentorganization_us', '4503', 'studentOrganizationsIds'),
      phiBetaKappa: this.getServiceData('he_studentorganization_us', '4504', 'studentOrganizationsIds'),
      physicallyChallenged: this.getServiceData('he_studentorganization_us', '4505', 'studentOrganizationsIds'),
      politicalActivism: this.getServiceData('he_studentorganization_us', '4506', 'studentOrganizationsIds'),
      preDental: this.getServiceData('he_studentorganization_us', '4507', 'studentOrganizationsIds'),
      preLaw: this.getServiceData('he_studentorganization_us', '4508', 'studentOrganizationsIds'),
      preMedical: this.getServiceData('he_studentorganization_us', '4509', 'studentOrganizationsIds'),
      preVeterinary: this.getServiceData('he_studentorganization_us', '4510', 'studentOrganizationsIds'),
      radioStation: this.getServiceData('he_studentorganization_us', '4511', 'studentOrganizationsIds'),
      religious: this.getServiceData('he_studentorganization_us', '4512', 'studentOrganizationsIds'),
      sportsIntramurals: this.getServiceData('he_studentorganization_us', '4513', 'studentOrganizationsIds'),
      studentGovernment: this.getServiceData('he_studentorganization_us', '4514', 'studentOrganizationsIds'),
      substanceAbuseSupport: this.getServiceData('he_studentorganization_us', '4515', 'studentOrganizationsIds'),
      televisionStation: this.getServiceData('he_studentorganization_us', '4516', 'studentOrganizationsIds'),
      theatreDrama: this.getServiceData('he_studentorganization_us', '4517', 'studentOrganizationsIds'),
      yearbook: this.getServiceData('he_studentorganization_us', '4518', 'studentOrganizationsIds'),
      phiThetaKappa: this.getServiceData('he_studentorganization_us', '4519', 'studentOrganizationsIds'),
    },
    athletics: this.getAtletics(),
  });

  getAdmissions = () => {
    const { admissionsPolicies } = this;
    const { registrationPolicies } = this;
    const { notificationPolicies } = this;
    const { earlyDecisionsValues } = this;
    const { waitingListValues } = this;
    const { fieldHeIntlbaccalaureateplcmidValues } = this; // new
    const { fieldHeClepsubjectplacementIdValues } = this; // new
    const { fieldHeCeebadvancedplacementidValues } = this; // new
    const { fieldHeApcreditValues } = this; // new
    const { fieldHeClepcreditValues } = this; // new
    const { fieldHeIbCreditsValues } = this; // new

    return {
      acceptanceRate: {
        applicationsAccepted: this.getDataByField('he_undergraduate_admissions_us', 'field_he_applications_accepted'),
        applicationsReceived: this.getDataByField('he_undergraduate_admissions_us', 'field_he_applications_received'),
      },

      importantPolicies: {
        admissionsPolicy: {
          ...this.getDataByField('he_undergraduate_admissions_us', 'field_he_admissionspolicy_id'),
          options: admissionsPolicies,
        },
        notificationPolicy: {
          ...this.getDataByField('he_undergraduate_admissions_us', 'field_he_notificationpolicy_id'),
          options: notificationPolicies,
        },
        registrationPolicy: {
          ...this.getDataByField('he_undergraduate_admissions_us', 'field_he_registrationpolicy_id'),
          options: registrationPolicies,
        },
        earlyDecision: {
          ...this.getDataByField('he_undergraduate_admissions_us', 'field_he_earlydecision'),
          options: earlyDecisionsValues,
        },
        waitingList: {
          ...this.getDataByField('he_undergraduate_admissions_us', 'field_he_waitinglistused'),
          options: waitingListValues,
        },
      },

      he_undergraduate_admissions_us: {
        field_he_intlbaccalaureateplcmid: { // new
          ...this.getDataByField('he_undergraduate_admissions_us', 'field_he_intlbaccalaureateplcmid'),
          options: fieldHeIntlbaccalaureateplcmidValues,
        },
        field_he_clepsubjectplacement_id: { // new
          ...this.getDataByField('he_undergraduate_admissions_us', 'field_he_clepsubjectplacement_id'),
          options: fieldHeClepsubjectplacementIdValues,
        },
        field_he_ceebadvancedplacementid: { // new
          ...this.getDataByField('he_undergraduate_admissions_us', 'field_he_ceebadvancedplacementid'),
          options: fieldHeCeebadvancedplacementidValues,
        },
        field_he_clepcredit: { // new
          ...this.getDataByField('he_undergraduate_admissions_us', 'field_he_clepcredit'),
          options: fieldHeClepcreditValues,
        },
        field_he_apcredit: { // new
          ...this.getDataByField('he_undergraduate_admissions_us', 'field_he_apcredit'),
          options: fieldHeApcreditValues,
        },
        field_he_ib_credits: { // new
          ...this.getDataByField('he_undergraduate_admissions_us', 'field_he_ib_credits'),
          options: fieldHeIbCreditsValues,
        },
      },

      deadlines: this.getDeadlines(this.deadlineTypes),

      fees: {
        applicationFee: this.getDataByField('he_undergraduate_admissions_us', 'field_he_applicationfee'),
        deposit: this.getDataByField('he_undergraduate_admissions_us', 'field_he_deposit'),
        transferDepositFee: this.getDataByField('he_undergraduate_admissions_us', 'field_he_transferappdeposit'),
        transferApplicationFee: this.getDataByField('he_undergraduate_admissions_us', 'field_he_transferapplicationfee'),
        internationalApplicationFee: this.getDataByField('he_internationaladmissions_us', 'field_he_applicationfee'),
        internationalDepositFee: this.getDataByField('he_internationaladmissions_us', 'field_he_deposit'),
      },

      qualification_data: this.getAllQualificationData(), // new

      applicationRequirements: {
        freshman: this.getApplicationRequirements(0),
        transfer: this.getApplicationRequirements(1),
        international: this.getApplicationRequirements(3),
      },

      recommendedCourses: this.getRecommendedCourses(this.prerequisiteTypes),

      applicationFactors: this.getApplicationFactors(),

      field_he_testoptional: this.getDataByField('he_undergraduate_admissions_us', 'field_he_testoptional'), // new

      field_he_job_plcmnt_rt_1_yr_gr: this.getDataByField('he_undergraduate_admissions_us', 'field_he_job_plcmnt_rt_1_yr_gr'), // new
      field_he_job_plcmnt_rt_less_1_yr: this.getDataByField('he_undergraduate_admissions_us', 'field_he_job_plcmnt_rt_less_1_yr'), // new
      field_he_job_plcmnt_rt_less_6_mo: this.getDataByField('he_undergraduate_admissions_us', 'field_he_job_plcmnt_rt_less_6_mo'), // new
      field_he_coalition_app_member: this.getDataByField('he_undergraduate_admissions_us', 'field_he_coalition_app_member'), // new
      field_he_common_app_member: this.getDataByField('he_undergraduate_admissions_us', 'field_he_common_app_member'), // new
    };
  }

  getCostDataLabel= (entity, fieldName, fieldValue) => {
    const metaData = get(entity, 'metaData', []);
    const field = metaData.find((item) => get(item, 'field_name', '') === fieldName) || {};
    return get(field, `allowed_values.${fieldValue}`, '');
  }

  getAllFinancialProfiles = () => { // new
    const contentType = 'field_he_financial_profile';
    const field = 'field_he_value';
    const profileType = 'field_he_financialaidprofiletype';

    const entity = this.sortedData[contentType];
    /* istanbul ignore else */
    if (entity && entity.fieldValues) {
      return entity.fieldValues.map((fieldItem, index) => {
        const profileTypeValue = get(fieldItem, `${profileType}.und[0].value`);
        const profileData = { ...this.getDataByField(contentType, field, index) };
        return {
          field_he_financialaidprofiletype: this.getCostDataLabel(this.sortedData[contentType], profileType, profileTypeValue),
          field_he_value: get(profileData, 'value', null),
        };
      });
    }
    return [];
  };

  getCostDataByProfileType = (profileTypeValue) => {
    const contentType = 'field_he_financial_profile';
    const field = 'field_he_value';
    const profileType = 'field_he_financialaidprofiletype';

    const entity = this.sortedData[contentType];
    /* istanbul ignore else */
    if (entity && entity.fieldValues) {
      const fieldValueIndex = entity.fieldValues.findIndex((fieldItem) =>
        get(fieldItem, `${profileType}.und[0].value`) === profileTypeValue);

      if (fieldValueIndex >= 0) {
        return {
          ...this.getDataByField(contentType, field, fieldValueIndex),
          label: this.getCostDataLabel(this.sortedData[contentType], profileType, profileTypeValue),
        };
      }
    }

    // Create new cost entity, if one does not exists already
    const entCollection = this.editor.getEntityCollectionByReference(
      'he_undergraduatefinancialaid_us',
      contentType,
      { field_he_finaidgroupappliedto: [{ value: '4' }] },
    );

    if (entCollection) {
      const values = { [profileType]: [{ value: profileTypeValue }] };
      const newEntity = entCollection.addEntity(values);
      return {
        eid: newEntity.eid,
        value: '',
        label: this.getCostDataLabel(get(this.sortedData, contentType, {}), profileType, profileTypeValue),
      };
    } else {
      return {
        value: '',
        label: this.getCostDataLabel(get(this.sortedData, contentType, {}), profileType, profileTypeValue),
      };
    }
  };

  getCosts = () => ({
    averageNetPrices: {
      '0-30': this.getCostDataByProfileType('6'),
      '30-48': this.getCostDataByProfileType('7'),
      '48-75': this.getCostDataByProfileType('8'),
      '75-110': this.getCostDataByProfileType('9'),
      '110+': this.getCostDataByProfileType('10'),
    },
    receivingAid: {
      grant: this.getCostDataByProfileType('11'),
      pellGrant: this.getCostDataByProfileType('12'),
      studentLoan: this.getCostDataByProfileType('13'),
    },
    averageAmountOfAid: {
      grant: this.getCostDataByProfileType('14'),
      pellGrant: this.getCostDataByProfileType('15'),
      studentLoan: this.getCostDataByProfileType('16'),
    },
    field_he_financial_profile: this.getAllFinancialProfiles(),
    costItems: { // new
      field_he_meets_full_fin_needs: {
        ...this.getDataByField('he_undergraduatefinancialaid_us', 'field_he_meets_full_fin_needs'),
        options: this.he_undergraduatefinancialaid_us_metaData.field_he_meets_full_fin_needs.allowed_values,
      },
      field_he_feeyear: this.getDataByField('he_undergraduatefinancialaid_us', 'field_he_feeyear'),
      field_he_books: this.getDataByField('he_undergraduatefinancialaid_us', 'field_he_books'),
      field_he_booksplus1yr: this.getDataByField('he_undergraduatefinancialaid_us', 'field_he_booksplus1yr'),
      field_he_instateflatfee: this.getDataByField('he_undergraduatefinancialaid_us', 'field_he_instateflatfee'),
      field_he_instateflatfeeplus1yr: this.getDataByField('he_undergraduatefinancialaid_us', 'field_he_instateflatfeeplus1yr'),
      field_he_instatetuition: this.getDataByField('he_undergraduatefinancialaid_us', 'field_he_instatetuition'),
      field_he_instatetuitionplus1yr: this.getDataByField('he_undergraduatefinancialaid_us', 'field_he_instatetuitionplus1yr'),
      field_he_outofstateflatfee: this.getDataByField('he_undergraduatefinancialaid_us', 'field_he_outofstateflatfee'),
      field_he_outofstateflatfeeplus1y: this.getDataByField('he_undergraduatefinancialaid_us', 'field_he_outofstateflatfeeplus1y'),
      field_he_outofstatetuition: this.getDataByField('he_undergraduatefinancialaid_us', 'field_he_outofstatetuition'),
      field_he_outofstatetuitionplus1y: this.getDataByField('he_undergraduatefinancialaid_us', 'field_he_outofstatetuitionplus1y'),
      field_he_percredithourchargeamnt: this.getDataByField('he_undergraduatefinancialaid_us', 'field_he_percredithourchargeamnt'),
      field_he_percredithourfeeplus1yr: this.getDataByField('he_undergraduatefinancialaid_us', 'field_he_percredithourfeeplus1yr'),
      field_he_typicalboardcharge: this.getDataByField('he_undergraduatefinancialaid_us', 'field_he_typicalboardcharge'),
      field_he_typicalboardchargeplus1: this.getDataByField('he_undergraduatefinancialaid_us', 'field_he_typicalboardchargeplus1'),
      field_he_typicalroomandboardchar: this.getDataByField('he_undergraduatefinancialaid_us', 'field_he_typicalroomandboardchar'),
      field_he_typicalroomboardcharge1: this.getDataByField('he_undergraduatefinancialaid_us', 'field_he_typicalroomboardcharge1'),
      field_he_typicalroomcharge: this.getDataByField('he_undergraduatefinancialaid_us', 'field_he_typicalroomcharge'),
      field_he_typicalroomchargeplus1y: this.getDataByField('he_undergraduatefinancialaid_us', 'field_he_typicalroomchargeplus1y'),
    },
  });

  getTransferInfo = () => {
    const transferRef = 'field_he_xfer_ref';
    const parentContentType = 'he_institution_us';
    const transferContentType = 'he_transfer_admissions';
    const parentEntity = this.sortedData[parentContentType].fieldValues[0];
    const ref = this.getDataOfItem(parentEntity, transferRef, 'target_id');

    // populate fieldValues to prevent UI from crashing
    const transferContent = this.sortedData[transferContentType];
    if (!transferContent.fieldValues.length) {
      const fieldValue = transferContent.metaData.reduce((acc, metaData) => {
        acc[metaData.field_name] = [];
        return acc;
      }, {});
      transferContent.fieldValues = [fieldValue];
    }

    // Create a new entity if it does not exist
    /* eslint-disable-next-line */
    if (!ref || ref == '37') {
      this.addTransferAdmissionsEntity(transferContentType, transferRef, parentContentType);
    }

    const getTransferCreditPolicies = () => {
      const fieldValue = this.getDataByField('he_transfer_admissions', 'field_he_xfer_adm_policies');
      const value = safeParse(fieldValue?.value, []);
      return {
        ...fieldValue, // eid and label
        rawValue: fieldValue?.value,
        value: value.sort(transferCreditPoliciesComparator),
      };
    };

    const getTransferAdmissionsContact = () => {
      const dataToFieldsMap = {
        namePrefix: 'field_he_xfer_c_name_pref',
        firstName: 'field_he_xfer_c_first_name',
        middleName: 'field_he_xfer_c_middle_name',
        lastName: 'field_he_xfer_c_last_name',
        jobDescription: 'field_he_xfer_c_job_desc',
        country: 'field_he_xfer_c_country_ref',
        state: 'field_he_xfer_c_state_ref',
        city: 'field_he_xfer_c_city',
        zip: 'field_he_xfer_c_post_code',
        street1: 'field_he_xfer_c_addr_line_1',
        street2: 'field_he_xfer_c_addr_line_2',
        email: 'field_he_xfer_c_email',
        fax: 'field_he_xfer_c_fax',
        phoneNumber: 'field_he_xfer_c_phone',
        tollFreeNumber: 'field_he_xfer_c_toll_free_no',
        url: 'field_he_xfer_c_url',
      };

      const getFieldValue = (field, name) => {
        const order = 0;
        if (name === 'state' || name === 'country') {
          const country = this.getDataByField(transferContentType, dataToFieldsMap.country, order, 'target_id', 'countriesVocabulary');
          const state = this.getDataByField(transferContentType, dataToFieldsMap.state, order, 'target_id');
          if (name === 'state') {
            const stateOptions = filterStatesByCountry(country.value, this.countriesVocabulary, this.statesVocabulary);
            return {
              ...state,
              options: stateOptions,
            };
          }
          return country;
        }
        if (name === 'url') {
          return this.getDataByField(transferContentType, field, 0, 'url');
        }
        return this.getDataByField(transferContentType, field);
      };

      return mapValues(dataToFieldsMap, (field, name) => omit(getFieldValue(field, name)/* , 'eid' */));
    };

    const getTransferApplicationRequirements = () => {
      const fieldValue = this.getDataByField('he_transfer_admissions', 'field_he_xfer_admissions_req');
      const value = safeParse(fieldValue?.value, []);
      return {
        ...fieldValue, // eid and label
        rawValue: fieldValue?.value,
        value: value.sort(transferApplicationRequirementsComparator),
      };
    };

    return ({
      transferAdmissionsContact: getTransferAdmissionsContact(),
      applicationRequirements: getTransferApplicationRequirements(),
      transferCreditPolicies: getTransferCreditPolicies(),
      overview: this.getDataByField('he_transfer_admissions', 'field_he_xfer_overview', 0),
      website: this.getDataByField('he_transfer_admissions', 'field_he_xfer_info_url', 0, 'url'),
      supportWebsite: this.getDataByField('he_transfer_admissions', 'field_he_xfer_support_url', 0, 'url'),
      financialAidInfoWebsite: this.getDataByField('he_transfer_admissions', 'field_he_xfer_fin_aid_url', 0, 'url'),
      creditTransferInfoWebsite: this.getDataByField('he_transfer_admissions', 'field_he_xfer_credit_info_url', 0, 'url'),
      academicResidency: this.getDataByField('he_transfer_admissions', 'field_he_xfer_min_home_credits', 0),
      articulationAgreements: this.getDataByField('he_transfer_admissions', 'field_he_xfer_artic_agr', 0),
      articulationAgreementSystems: this.getDataByField('he_transfer_admissions', 'field_he_xfer_artic_sys', 0, 'value', 'collegeSystems'),
      creditCap: this.getDataByField('he_transfer_admissions', 'field_he_xfer_credit_cap', 0),
      creditsNotAccepted: this.getDataByField('he_transfer_admissions', 'field_he_xfer_cr_not_acc', 0),
      dualAdmissionsPrograms: this.getDataByField('he_transfer_admissions', 'field_he_xfer_dual_admissions', 0),
      collegeSystems: this.getDataByField('he_institution_us', 'field_he_coll_uni_sys', 0, 'value', 'collegeSystems'),
      scholarshipAvailability: this.getDataByField('he_transfer_admissions', 'field_he_xfer_scholarships_avbl', 0),
      undergraduateStudents: this.getDataByField('he_transfer_admissions', 'field_he_xfer_in_enrollment', 0),
      eightYearGradRate: this.getDataByField('he_transfer_admissions', 'field_he_xfer_ft_8yr_grad_rate', 0),
      enrollmentTerms: this.getDataByField('he_transfer_admissions', 'field_he_xfer_sems_transfer', 0),
    });
  };

  addTransferAdmissionsEntity = (transferContentType, transferRef, transferRefParent) => {
    const dataToFieldsMap = [
      { name: 'field_workflow', initialEntityFieldValue: '0' },
      { name: 'field_he_xfer_students_accepted', initialEntityFieldValue: 'no-answer' },
      { name: 'field_he_xfer_scholarships_avbl', initialEntityFieldValue: 'no-answer' },
    ];

    const fields = {};
    dataToFieldsMap.forEach((map) => {
      fields[map.name] = map.initialEntityFieldValue;
    });

    const transferEntityCollection = this.editor.getEntityCollectionByType(
      transferContentType,
      fields,
    );
    const entity = transferEntityCollection.addEntity(fields);

    const transferRefParentEntityCollection = this.editor.getEntityCollectionByType(transferRefParent, {});
    const transferRefParentEntities = transferRefParentEntityCollection.getEntities();
    /* istanbul ignore else */
    if (transferRefParentEntities && transferRefParentEntities.length > 0) {
      this.editor.setValue(transferRefParentEntities[0].eid, transferRef, [{ target_id: entity.eid.replace('node', '') }], true);
    }

    // adding nid, required by the fields returned from getTransferInfo()
    /* istanbul ignore else */
    if (this.sortedData[transferContentType].fieldValues[0]) {
      this.sortedData[transferContentType].fieldValues[0].nid = entity.eid.replace('node', '');
    }
  }

  /* istanbul ignore next */
  updateState = () => ({
    overview: this.getOverview(),
    studies: this.getStudies(),
    studentLife: this.getStudentLife(),
    admissions: this.getAdmissions(),
    costs: this.getCosts(),
    transferInfo: this.getTransferInfo(),
  });

  getState = () => ({
    overview: this.getOverview(),
    studies: this.getStudies(),
    studentLife: this.getStudentLife(),
    admissions: this.getAdmissions(),
    costs: this.getCosts(),
    transferInfo: this.getTransferInfo(),
    hasRegisteredChanges: false,
  });
}
